import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import BackLink from "../../components/navigation/BackLink/BackLink";
import Spinner from "../../components/Spinner/Spinner";
import FilterSelect from "../../components/forms/FilterSelect";
import BoardGrid from "../../components/timetable/BoardGrid";
import UnplacedTray from "../../components/timetable/UnplacedTray";
import RunStatusPanel, { isRunInFlight } from "../../components/timetable/RunStatusPanel";
import CommitDialog from "../../components/timetable/CommitDialog";
import { codeMessage, runErrorMessage } from "../../components/timetable/runErrors";
import useBoard, { slotKey } from "../../components/timetable/useBoard";
import { cancelRun, stopRun } from "../../services/timetableGenerationService";
import { compareText } from "../../utils/helpers/locale";

// ---------------------------------------------------------------------------
// The aSc-style draft board for one generation run.
//
// Placement is click-to-select-then-click-to-place (no drag-and-drop library,
// and this way it is keyboard-operable): pick a card, every legal slot lights
// up indigo and every clashing one goes dashed rose, then click a slot. Escape
// cancels. Clicking the tray sends the card back to weekday:null/period:null.
//
// Commit is destructive — it replaces the school's ENTIRE active grid, hand-
// entered and CSV-imported rows included — so it always goes through the
// preview-numbers confirmation below, never a bare button.
// ---------------------------------------------------------------------------

// Run status/diagnostics rendering, the destructive-commit dialog and the
// error-code vocabulary are shared with the generate page — imported above, not
// re-implemented here, so there is one commit confirmation in the product.

const VIEWS = ["class", "teacher", "subject"];

export default function BoardPage() {
  const { uuid } = useParams();
  const { t } = useTranslation();

  const {
    run,
    status,
    placements,
    unplaced,
    stats,
    loading,
    loadError,
    reload,
    weekdays,
    periods,
    selected,
    selectedUuid,
    select,
    clearSelection,
    targetMap,
    busyUuid,
    moveTo,
    sendToTray,
    toggleLock,
    undo,
    canUndo,
    actionError,
    dismissError,
  } = useBoard(uuid);

  const [view, setView] = useState("class");
  const [entity, setEntity] = useState("");
  const [localError, setLocalError] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [stopping, setStopping] = useState(false);

  const editable = Boolean(run?.is_reviewable);
  const inFlight = isRunInFlight(status);

  // ------------------------------------------------------------- view options
  const options = useMemo(() => {
    const classes = new Map();
    const teachers = new Map();
    const subjects = new Map();
    placements.forEach((card) => {
      if (card.group) classes.set(card.group, card.group_name || card.group);
      (card.teachers || []).forEach((teacher) => teachers.set(teacher.uuid, teacher.name));
      const code = card.subject_code || card.subject;
      if (code) subjects.set(code, card.subject || code);
    });
    const toList = (map) =>
      [...map.entries()]
        .map(([value, label]) => ({ value, label }))
        .sort((a, b) => compareText(a.label, b.label));
    return { class: toList(classes), teacher: toList(teachers), subject: toList(subjects) };
  }, [placements]);

  // Memoized, not `options[view] || []`: the bare fallback would mint a new
  // array every render and re-fire the picker effect below in a loop.
  const currentOptions = useMemo(() => options[view] || [], [options, view]);

  // Keep the picker on a real option whenever the view or the board changes.
  useEffect(() => {
    if (currentOptions.length === 0) {
      setEntity("");
      return;
    }
    setEntity((current) =>
      currentOptions.some((option) => option.value === current)
        ? current
        : currentOptions[0].value,
    );
  }, [currentOptions]);

  const visibleCards = useMemo(() => {
    if (!entity) return [];
    return placements.filter((card) => {
      if (view === "class") return card.group === entity;
      if (view === "teacher") return (card.teachers || []).some((x) => x.uuid === entity);
      return (card.subject_code || card.subject) === entity;
    });
  }, [placements, view, entity]);

  // ---------------------------------------------------------------- handlers
  const conflictLabel = useCallback(
    (conflict) =>
      conflict.kind === "overflow"
        ? t("timetable.clashOverflow", { count: Number(conflict.label) })
        : t(conflict.kind === "class" ? "timetable.clashClass" : "timetable.clashTeacher", {
            name: conflict.label,
          }),
    [t],
  );

  const handleGridCardClick = useCallback(
    (card) => {
      if (!editable) return;
      setLocalError(null);
      // A card already selected? Then clicking any other card means "put it
      // in that slot" — the server is the authority on whether that is legal.
      if (selectedUuid && selectedUuid !== card.uuid) {
        moveTo(selectedUuid, card.weekday, card.period);
        return;
      }
      select(card.uuid);
    },
    [editable, selectedUuid, moveTo, select],
  );

  const handleTrayCardClick = useCallback(
    (card) => {
      if (!editable) return;
      setLocalError(null);
      select(card.uuid);
      // Follow the card into view, otherwise you would be staring at another
      // class's week with nowhere legal to put it.
      if (view === "class" && card.group) setEntity(card.group);
      else if (view === "teacher" && card.teachers?.[0]) setEntity(card.teachers[0].uuid);
      else if (view === "subject") setEntity(card.subject_code || card.subject || "");
    },
    [editable, select, view],
  );

  const handleCellClick = useCallback(
    (weekday, period, state) => {
      if (!selectedUuid) return;
      setLocalError(null);
      if (state === "invalid") {
        // Client-side pre-check matched the server rule — explain it rather
        // than firing a PATCH we already know will 400.
        const conflicts = targetMap?.get(slotKey(weekday, period)) || [];
        setLocalError({
          code: "slot_taken",
          detail: null,
          details: conflicts.map(conflictLabel),
        });
        return;
      }
      moveTo(selectedUuid, weekday, period);
    },
    [selectedUuid, targetMap, conflictLabel, moveTo],
  );

  const handleSendToTray = useCallback(() => {
    if (!selectedUuid) return;
    setLocalError(null);
    sendToTray(selectedUuid);
  }, [selectedUuid, sendToTray]);

  const handleToggleLock = useCallback(
    (card) => {
      if (!editable) return;
      setLocalError(null);
      toggleLock(card.uuid);
    },
    [editable, toggleLock],
  );

  const doCancel = useCallback(async () => {
    setCancelling(true);
    try {
      await cancelRun({ uuid });
      reload();
    } catch (err) {
      toast.error(runErrorMessage(err, t, t("timetable.cancelFailed")));
    } finally {
      setCancelling(false);
    }
  }, [uuid, t, reload]);

  // Stop searching but KEEP the board. No `reload()` — the run stays in flight
  // until the worker notices, and `useBoard`'s own poll carries it to its
  // terminal state; refetching here would just show the same in-flight row.
  const doStop = useCallback(async () => {
    setStopping(true);
    try {
      await stopRun({ uuid });
      toast.success(t("timetable.stopRequested"));
    } catch (err) {
      toast.error(runErrorMessage(err, t, t("timetable.stopFailed")));
    } finally {
      setStopping(false);
    }
  }, [uuid, t]);

  const banner = actionError || localError;

  return (
    <div className="p-6 bg-slate-50 min-h-full">
      <div className="max-w-[1600px] mx-auto space-y-5">
        <BackLink to="/timetable/generate" labelKey="timetable.backToRuns" />

        {loading ? (
          <BoardSkeleton />
        ) : loadError || !run ? (
          <div className="rounded-2xl bg-white border border-slate-200 p-10 text-center">
            <p className="text-sm font-bold text-slate-700">{t("timetable.runLoadFailed")}</p>
            <button
              type="button"
              onClick={reload}
              className="mt-4 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
            >
              {t("common.retry")}
            </button>
          </div>
        ) : (
          <>
            <BoardHeader
              stats={stats}
              inFlight={inFlight}
              reviewable={editable}
              cancelling={cancelling}
              stopping={stopping}
              onCancel={doCancel}
              onStop={doStop}
              onCommit={() => setCommitting(true)}
            />

            {/* Status, elapsed time, solver stats and — crucially — the
                precheck findings that name WHY the week does not fit. */}
            <RunStatusPanel run={run} />

            {inFlight ? (
              <div className="rounded-2xl border border-slate-200 bg-white py-20">
                <Spinner label={t("timetable.inFlightLabel")} size="lg" />
                <p className="mt-4 text-center text-xs font-semibold text-slate-400">
                  {t("timetable.inFlightHint")}
                </p>
              </div>
            ) : (
              <>
                <Toolbar
                  view={view}
                  onViewChange={(next) => {
                    setLocalError(null);
                    clearSelection();
                    setView(next);
                  }}
                  options={currentOptions}
                  entity={entity}
                  onEntityChange={setEntity}
                  canUndo={editable && canUndo}
                  onUndo={undo}
                />

                {selected && (
                  <div className="flex items-center gap-3 flex-wrap rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-2.5">
                    <span className="text-[11px] font-black uppercase tracking-widest text-indigo-700">
                      {t("timetable.movingCard", {
                        subject: selected.subject || "—",
                        group: selected.group || "—",
                      })}
                    </span>
                    <span className="text-[11px] font-semibold text-indigo-500">
                      {t("timetable.movingHint")}
                    </span>
                    <button
                      type="button"
                      onClick={clearSelection}
                      className="ml-auto px-3 py-1 rounded-lg text-[11px] font-black uppercase tracking-widest text-indigo-700 hover:bg-indigo-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
                    >
                      {t("common.cancel")}
                    </button>
                  </div>
                )}

                {banner && (
                  <ClashBanner
                    banner={banner}
                    onDismiss={() => {
                      dismissError();
                      setLocalError(null);
                    }}
                  />
                )}

                {!editable && (
                  <p className="text-[11px] font-black text-slate-500 bg-slate-100 border border-slate-200 rounded-xl px-4 py-3">
                    {t("timetable.readOnlyNotice")}
                  </p>
                )}

                <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_320px] gap-5 items-start">
                  {currentOptions.length === 0 ? (
                    <EmptyBoard />
                  ) : (
                    <BoardGrid
                      weekdays={weekdays}
                      periods={periods}
                      cards={visibleCards}
                      view={view}
                      selectedUuid={selectedUuid}
                      targetMap={targetMap}
                      busyUuid={busyUuid}
                      readOnly={!editable}
                      onCardClick={handleGridCardClick}
                      onCellClick={handleCellClick}
                      onToggleLock={handleToggleLock}
                    />
                  )}

                  <UnplacedTray
                    cards={unplaced}
                    view={view}
                    selectedUuid={selectedUuid}
                    busyUuid={busyUuid}
                    readOnly={!editable}
                    canReceive={Boolean(editable && selected && selected.weekday != null)}
                    onCardClick={handleTrayCardClick}
                    onSendToTray={handleSendToTray}
                    onToggleLock={handleToggleLock}
                  />
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Destructive: replaces the school's ENTIRE active grid, hand-entered
          and CSV-imported rows included. CommitDialog loads commit-preview and
          gates the confirm behind an explicit acknowledgement. */}
      {committing && (
        <CommitDialog
          runUuid={uuid}
          onClose={() => setCommitting(false)}
          onCommitted={reload}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

// Board-specific chrome: the card counts a manager scans first, plus the two
// run-level actions. Everything about the RUN itself (status, solver stats,
// precheck findings) is RunStatusPanel's job, and committing is CommitDialog's.
function BoardHeader({
  stats, inFlight, reviewable, cancelling, stopping, onCancel, onStop, onCommit,
}) {
  const { t } = useTranslation();
  return (
    <div className="bg-slate-900 rounded-[28px] p-6 sm:p-8 text-white">
      <div className="flex items-start gap-4 flex-wrap">
        <div className="min-w-0">
          <h2 className="text-xl font-black tracking-tight">{t("timetable.boardTitle")}</h2>
          <p className="text-sm text-white/60 font-medium mt-1">{t("timetable.boardSubtitle")}</p>
        </div>

        <div className="ml-auto flex items-center gap-2">
          {inFlight && (
            <>
              {/* Two outcomes, labelled by outcome rather than by verb: one
                  keeps the week found so far, the other throws it away. */}
              <button
                type="button"
                onClick={onCancel}
                disabled={cancelling || stopping}
                className="px-5 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 border border-white/10 text-[11px] font-black uppercase tracking-widest disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
              >
                {cancelling ? t("timetable.cancelling") : t("timetable.cancelRun")}
              </button>
              <button
                type="button"
                onClick={onStop}
                disabled={stopping || cancelling}
                className="px-5 py-2.5 rounded-xl bg-indigo-500 hover:bg-indigo-400 text-white text-[11px] font-black uppercase tracking-widest disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
              >
                {stopping ? t("timetable.stopping") : t("timetable.stopAndKeep")}
              </button>
            </>
          )}
          {!inFlight && reviewable && (
            <button
              type="button"
              onClick={onCommit}
              // Rose, not indigo: this replaces the school's whole live grid.
              // Same key + tint as the generate page's commit CTA.
              className="px-6 py-2.5 bg-rose-600 text-white rounded-xl text-sm font-semibold hover:bg-rose-700 shadow-lg shadow-rose-900/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
            >
              {t("timetable.commitConfirm")}
            </button>
          )}
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-6">
        <Stat label={t("timetable.statPlaced")} value={`${stats.placed}/${stats.total}`} />
        <Stat
          label={t("timetable.statUnplaced")}
          value={stats.unplaced}
          tone={stats.unplaced > 0 ? "text-amber-300" : "text-emerald-300"}
        />
        <Stat label={t("timetable.statLocked")} value={stats.locked} />
      </div>
    </div>
  );
}

function Stat({ label, value, tone = "text-white" }) {
  return (
    <div>
      <p className="text-[10px] font-black uppercase tracking-widest text-white/40">{label}</p>
      <p className={`text-xl font-black mt-0.5 ${tone}`}>{value}</p>
    </div>
  );
}


// A rejected move. For `slot_taken` the backend names the clashing class /
// teacher in `details` — show those, never a generic "something went wrong".
function ClashBanner({ banner, onDismiss }) {
  const { t } = useTranslation();
  // `code` first (the stable contract), then the server's own prose. Never a
  // regex over English — see runErrors.js.
  const mapped = codeMessage(banner.code, t);
  return (
    <div
      role="alert"
      className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 flex items-start gap-3"
    >
      <div className="min-w-0">
        <p className="text-[11px] font-black uppercase tracking-widest text-rose-700">
          {mapped || banner.detail || t("timetable.errMoveFailed")}
        </p>
        {banner.details.length > 0 && (
          <ul className="mt-1.5 flex flex-wrap gap-1.5">
            {banner.details.map((detail, index) => (
              <li
                key={index}
                className="px-2 py-0.5 rounded-md bg-white border border-rose-200 text-[11px] font-bold text-rose-700"
              >
                {detail}
              </li>
            ))}
          </ul>
        )}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t("common.close")}
        className="ml-auto p-1 rounded-lg text-rose-400 hover:text-rose-700 hover:bg-rose-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-500"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

function Toolbar({
  view, onViewChange, options, entity, onEntityChange, canUndo, onUndo,
}) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-3 flex-wrap">
      <div
        role="group"
        aria-label={t("timetable.viewAria")}
        className="inline-flex rounded-xl border border-slate-200 bg-white p-1 shadow-sm"
      >
        {VIEWS.map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => onViewChange(option)}
            aria-pressed={view === option}
            className={`px-4 py-1.5 rounded-lg text-[11px] font-black uppercase tracking-widest transition focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 ${
              view === option
                ? "bg-indigo-600 text-white shadow-sm"
                : "text-slate-500 hover:text-slate-800 hover:bg-slate-50"
            }`}
          >
            {t(`timetable.view${option.charAt(0).toUpperCase()}${option.slice(1)}`)}
          </button>
        ))}
      </div>

      <FilterSelect
        options={options}
        value={entity}
        onChange={onEntityChange}
        ariaLabel={t(`timetable.pick${view.charAt(0).toUpperCase()}${view.slice(1)}`)}
        placeholder={t(`timetable.pick${view.charAt(0).toUpperCase()}${view.slice(1)}`)}
        minWidthClass="min-w-[14rem]"
      />

      {/* Every click on this board writes to the server immediately, so a
          mis-drop on a 190-card week was unrecoverable except by regenerating
          and losing every other edit. Ctrl/Cmd+Z does the same thing. */}
      <button
        type="button"
        onClick={onUndo}
        disabled={!canUndo}
        title={t("timetable.undoHint")}
        className="ml-auto px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-[11px] font-black uppercase tracking-widest text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
      >
        {t("timetable.undo")}
      </button>

      <p className="text-[11px] font-semibold text-slate-500">
        {t("timetable.lockHint")}
      </p>
    </div>
  );
}

function EmptyBoard() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center text-center py-20 px-6 rounded-2xl border-2 border-dashed border-slate-200 bg-white/50">
      <span className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
        <svg
          className="w-7 h-7 text-slate-400"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M8 7V3m8 4V3M3 11h18M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      </span>
      <p className="text-sm font-bold text-slate-700">{t("timetable.noCards")}</p>
      <p className="mt-1 text-xs text-slate-400 max-w-xs">{t("timetable.noCardsHint")}</p>
    </div>
  );
}

function BoardSkeleton() {
  return (
    <div className="space-y-5" aria-hidden="true">
      <div className="h-40 rounded-[28px] bg-slate-200/70 animate-pulse" />
      <div className="h-10 w-96 rounded-xl bg-slate-200/70 animate-pulse" />
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_320px] gap-5">
        <div className="rounded-2xl border border-slate-200 bg-white p-3">
          <div className="grid grid-cols-6 gap-1">
            {Array.from({ length: 48 }).map((_, i) => (
              <div key={i} className="h-14 rounded-lg bg-slate-100 animate-pulse" />
            ))}
          </div>
        </div>
        <div className="h-72 rounded-2xl bg-slate-200/70 animate-pulse" />
      </div>
    </div>
  );
}
